/**
 * The company's language — rung (d), and the only rung that reaches the sign-in page.
 *
 * WHY THIS EXISTS AT ALL, given that a person can set their own. Because on the sign-in page there IS
 * no person: nobody is logged in, so no preference can be consulted, and the device guess is
 * unreliable in exactly the way that matters — almost every handset on a US job site reports `en-US`
 * whatever its owner reads. A company that knows its crews read Spanish needs one place to say so
 * once, and this is it.
 *
 * WHY IT SITS AT THE BOTTOM OF THE CHAIN ANYWAY. It is wrong for a mixed office, which is most
 * offices: the same company has a Spanish-reading crew and an English-reading book-keeper. So a
 * person's own setting and the choice made on a device both outrank it, and the copy below says so
 * rather than leaving an admin to discover it.
 *
 * "No company language" is a real answer, not an absence of one — it hands the sign-in page back to
 * whatever the visitor's device reports, which is the right default for a company that has not
 * thought about it.
 */
import React, { useCallback, useEffect, useState } from 'react'

import { identityService } from '../api'
import { useTenantBranding } from '../../contexts/TenantContext.jsx'
import { LANGUAGE_NAMES, languageName, selectedLanguage } from './languages'
import { useT } from './LanguageContext.jsx'

export default function CompanyLanguageSetting({ canEdit = true }) {
  const t = useT('settings')
  const branding = useTenantBranding()
  const [value, setValue] = useState(branding?.defaultLanguage || '')
  const [status, setStatus] = useState(null)

  useEffect(() => { setValue(branding?.defaultLanguage || '') }, [branding?.defaultLanguage])

  const save = useCallback(async (tag) => {
    setValue(tag)
    setStatus(null)
    try {
      await identityService.setTenantDefaultLanguage(branding.tenantId, tag || null)
      setStatus('saved')
      // Re-read branding so the sign-in page (and this session) pick it up without a hard reload —
      // the tenant default arrives on that payload and nowhere else.
      branding.refresh?.()
    } catch {
      setStatus('failed')
      setValue(branding?.defaultLanguage || '')
    }
  }, [branding])

  if (!branding?.tenantId) return null

  // A stored `es-MX` is Spanish and is shown as Spanish; a stored tag this application carries no
  // dictionary for gets an option of its own rather than falling through to "No company language",
  // because a dropdown that reports a live setting as absent invites an admin to overwrite one they
  // were never shown. See selectedLanguage.
  const selected = selectedLanguage(value)
  const unknown = Boolean(selected) && !LANGUAGE_NAMES[selected]

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-900">{t('companyDefaultLanguage')}</h3>
      <p className="text-xs text-gray-500 mt-1 mb-3">{t('companyDefaultLanguageHelp')}</p>
      <select
        aria-label={t('companyDefaultLanguage')}
        value={selected}
        disabled={!canEdit}
        onChange={(e) => save(e.target.value)}
        className="border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-800 disabled:opacity-60"
      >
        <option value="">{t('noCompanyDefault')}</option>
        {unknown && <option value={selected}>{languageName(selected)}</option>}
        {Object.keys(LANGUAGE_NAMES).map((tag) => (
          <option key={tag} value={tag}>{languageName(tag)}</option>
        ))}
      </select>
      {/* THE STICKER SHEET HAS ITS OWN STARTING LANGUAGE and this is not it. The person at a machine
          has no account, so that page answers a different question for a different population, and
          the two settings have never known about each other. Naming it here is what stops an admin
          setting this one and reasonably believing the QR sheets moved too. */}
      <p className="text-xs text-gray-500 mt-2">{t('companyDefaultLanguageNotStickers')}</p>
      {status === 'saved' && <p className="text-xs text-green-700 mt-2">{t('languageSaved')}</p>}
      {status === 'failed' && <p className="text-xs text-red-700 mt-2">{t('languageSaveFailed')}</p>}
    </div>
  )
}
